<template>
    <div class="md:pl-10 w-full md:pr-10">
        <div class="mb-6">
            <label for="image" ref="image">
                Resource Photo <span class="text-red">*</span>
                <div class="text-sm font-sans-book mb-5">Please upload a horizontal/landscape image (JPEG or PNG), ideally 1424 pixels x 862 pixels. Image must NOT have any text. You can also drag and drop directly onto the Choose File button.</div>
            </label>
            <div v-if="resource && resource.image">
                <img :src="resource.image" class="h-48 mb-2" />
            </div>

            <input id="image" type="hidden" name="image" v-validate="imageValidate" v-model="form.newImage">

            <vue-dropzone
                ref="dropzone"
                id="dropzone"
                :options="dropzoneOptions"
                @vdropzone-success="uploaded"
                @vdropzone-error="updateImageErrorMessage"
                @vdropzone-removed-file="resetImage"
                :useCustomSlot=true
            >
                <p>You may drag-and-drop an image here. File size must be less than 4 MB.</p>
            </vue-dropzone>
            <span v-if="errors.has('image')" class="text-lg error text-error-red">{{ errors.first('image') }}</span>
            <span v-if="imageError" class="text-lg error text-error-red">{{ imageError }}</span>
        </div>

        <div class="mb-6">
            <label for="teaser" ref="teaser">
                Teaser <span class="text-red">*</span>
                <div class="text-sm font-sans-book">170 characters maximum, including spaces. This brief teaser copy of your resource listing on the search results page. It should be enticing and make it clear to visitors what your particular resource is about.</div>
            </label>
            <textarea
                v-model="form.teaser"
                v-validate="'required|max:170'"
                name="teaser"
                class="w-full mt-3 p-4 overflow-y-scroll border border-black h-48 bg-transparent"
            ></textarea>
            <div class="text-sm font-sans-italic text-gray-dark">Character count: {{ characterCount }}</div>
            <span v-show="errors.has('teaser')" class="error text-error-red text-lg">{{ errors.first('teaser') }}</span>
        </div>

        <div class="mb-6">
            <label for="description" ref="description">
                Enter Resource Information Here <span class="text-red">*</span>
                <div class="text-sm font-sans-book mb-2">Use this field to provide visitors a compelling description of your resource or to write a brand new resource right here (an article, an essay, a poem, you name it!)</div>
                <div class="text-sm font-sans-book">Please note that all hyperlinks MUST begin with https:// in order for them to link properly. For example, type in https://letsreimagine.org rather than letsreimagine.org.</div>            </label>
            <vue-editor v-model="form.description" :editorToolbar="descriptionToolbar" v-validate="'required'" name="description"></vue-editor>
            <span v-show="errors.has('description')" class="error text-error-red text-lg">{{ errors.first('description') }}</span>
        </div>

        <div class="mb-6">
            <label for="teaser">
                What inspired this?
                <div class="text-sm font-sans-book">Posts that have personal backstories are more engaging and meaningful. Who or what inspired you to create what you’ve shared here? How did you come up with it? Be vivid. This is your chance to celebrate who or what is at the root of your offering.</div>
            </label>
            <textarea v-model="form.inspiration" name="inspiration" class="w-full mt-3 p-4 overflow-y-scroll border border-black h-48 bg-transparent"></textarea>
        </div>

        <div class="mb-6 flex flex-wrap justify-start md:justify-end">
          <button class="button" @click.stop.prevent="save" :class="{ 'opacity-50 cursor-not-allowed': saving }" :disabled="saving">
            <template v-if="saving">Processing...</template>
            <template v-else>Save</template>
          </button>
          <div class="pr-0 md:pr-4 pb-4 md:pb-0 w-full md:w-auto" />
          <button class="button" @click.stop.prevent="saveNext" :class="{ 'opacity-50 cursor-not-allowed': saving }" :disabled="saving">Save & Next</button>
        </div>

    </div>
</template>

<script>
    import { mapGetters } from "vuex";
    import { VueEditor, Quill } from "vue2-editor";
    import resourceSave from "../../../mixins/resource-save";
    import messaging from "../../../mixins/messaging";
    import vue2Dropzone from "vue2-dropzone";
    import "vue2-dropzone/dist/vue2Dropzone.min.css";
    import validation from "../../../mixins/validation";

    export default {
        name: "ResourceCreateDetails",
        mixins: [resourceSave, messaging, validation],
        components: {
            VueEditor,
            vueDropzone: vue2Dropzone
        },
        data: () => ({
            descriptionToolbar: [
                ["bold", "italic", "link"],
                [{ list: "ordered" }, { list: "bullet" }]
            ],
            form: {
                teaser: "",
                description: "",
                newImage: "",
                inspiration: ""
            },
            imageError: ""
        }),
        computed: {
            ...mapGetters({
                isLoading: "resourceIsLoading",
                resource: "resource",
                tokens: "tokens",
                user: "user"
            }),
            characterCount() {
                return this.form.teaser ? this.form.teaser.length : 0;
            },
            dropzoneOptions() {
                return {
                    addRemoveLinks: true,
                    acceptedFiles: ".png,.jpg, image/png, image/jpeg",
                    headers: {
                        "X-Csrf-Token": document.head.querySelector(
                            'meta[name="csrf-token"]'
                        ).content,
                        Authorization: "Bearer " + this.tokens.access_token
                    },
                    maxFiles: 1,
                    maxFilesize: 4, // in Mb
                    thumbnailWidth: "247",
                    thumbnailHeight: "143",
                    url: this.$laroute.route("api.temp-images.store"),
                    withCredentials: true
                };
            },
            imageValidate() {
                if ((this.resource && this.resource.image) || this.form.newImage) {
                    return "";
                }
                return "required";
            },
            next() {
                return this.user.isAdmin ? "resource-tags" : "resource-submit";
            }
        },
        watch: {
            resource(resource) {
                if (!_.isEmpty(resource)) {
                    this.form.image = resource.image;
                    this.form.teaser = resource.teaser;
                    this.form.description = resource.description;
                    this.form.inspiration = resource.inspiration;

                    this.$nextTick(function() {
                        this.$store.dispatch("setUnsavedData", false);
                    });
                }
            }
        },
        methods: {
            resetImage(file) {
                this.imageError = "";

                axios
                    .delete(this.$laroute.route("api.temp-images.destroy"), {
                        data: { filePath: this.form.newImage }
                    })
                    .then(response => {
                        this.form.newImage = "";
                    })
                    .catch(error => {
                        this.displayError(error);
                    });
            },
            save() {
                this.saving = true;
                this.$store.dispatch("setResourceIsLoading", true);

                return this.$validator.validateAll().then(result => {
                    if (!result) {
                        this.saving = false;
                        this.gotoFirstValidationError();
                        return;
                    }

                    this.$toasted.show("Resource currently saving...");

                    let route = this.$laroute.route(
                        "api.resources.update.details",
                        { resource: this.resource.id }
                    );

                    return axios
                        .put(route, this.form)
                        .then(response => {
                            this.$toasted.show("Resource saved.");
                            this.saving = false;

                            let resourceResponse = response.data.data;
                            this.$store.dispatch(
                                "setActiveResource",
                                response.data
                            );
                            this.$store.dispatch(
                                "setResourceValidation",
                                resourceResponse
                            );
                            this.$store.dispatch("setUnsavedData", false);
                            this.$store.dispatch("setResourceIsLoading", false);

                            this.$refs.dropzone.removeAllFiles();
                        })
                        .catch(error => {
                            this.displayError(error);
                        });
                });
            },
            updateImageErrorMessage(file, message) {
                this.imageError = message;
            },
            uploaded(file, path) {
                this.form.newImage = path;
            }
        },
        mounted() {
            this.$store.dispatch("getResource", { id: this.$route.params.id });
        }
    };
</script>
